import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Lock, PlayCircle, BookOpen, TrendingUp, Brain, Sigma, Shapes } from "lucide-react";

const CAT_LABEL = { verbal: "Verbale", abstract: "Astratto", numerical: "Numerico", mixed: "Misto" };
const CAT_META = {
  verbal: { icon: Brain, label: "Ragionamento Verbale" },
  numerical: { icon: Sigma, label: "Ragionamento Numerico" },
  abstract: { icon: Shapes, label: "Ragionamento Astratto" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const [cats, setCats] = useState([]);
  const [hasAll, setHasAll] = useState(false);
  const [attempts, setAttempts] = useState([]);

  useEffect(() => {
    api.get("/practice").then(r => { setCats(r.data.categories); setHasAll(r.data.has_all); }).catch(()=>{});
    api.get("/attempts/mine").then(r => setAttempts(r.data)).catch(()=>{});
  }, []);

  if (!user) return <Layout><div className="max-w-5xl mx-auto p-8"><Link to="/login" className="underline">Accedi per continuare →</Link></div></Layout>;

  const avg = attempts.length ? (attempts.reduce((a, b) => a + b.score_percent, 0) / attempts.length).toFixed(1) : "—";

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="mb-10">
          <div className="label-mono text-[#002FA7] mb-2">— {t("dashboard_hi")}</div>
          <h1 className="text-4xl lg:text-5xl font-black tracking-tighter">{user.name || user.email}</h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          <StatCard label="Tentativi" value={attempts.length} icon={PlayCircle} />
          <StatCard label="Punteggio medio" value={avg + (avg !== "—" ? "%" : "")} icon={TrendingUp} />
          <StatCard label="Acquisti attivi" value={(user.purchases || []).length} icon={BookOpen} />
        </div>

        <Tabs defaultValue="tests" className="w-full">
          <TabsList className="rounded-none border border-slate-200 bg-white p-0">
            <TabsTrigger value="tests" className="rounded-none label-mono data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-tests">{t("tab_tests")}</TabsTrigger>
            <TabsTrigger value="history" className="rounded-none label-mono data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-history">{t("tab_history")}</TabsTrigger>
            <TabsTrigger value="purchases" className="rounded-none label-mono data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-purchases">{t("tab_purchases")}</TabsTrigger>
          </TabsList>

          <TabsContent value="tests" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {["verbal", "numerical", "abstract"].map((cat) => {
                const info = cats.find((c) => c.category === cat) || {};
                const Icon = CAT_META[cat].icon;
                const unlocked = hasAll || (info.premium_unlocked || 0) > 0;
                return (
                  <div key={cat} data-testid={`dash-test-card-${cat}`} className="border border-slate-200 bg-white p-6 flex flex-col hover:shadow-hard transition-all hover:-translate-y-1">
                    <Icon className="w-9 h-9 text-[#002FA7] mb-4" strokeWidth={1.5} />
                    <h3 className="font-display font-bold text-lg mb-4">{CAT_META[cat].label}</h3>
                    {unlocked ? (
                      <Link to={`/premium/${cat}`} className="mb-3">
                        <Button data-testid={`dash-premium-${cat}`} className="w-full rounded-none bg-[#002FA7] hover:bg-[#001d6b] label-mono py-5">
                          <PlayCircle className="w-4 h-4 mr-2" />Allenamento ({hasAll ? info.premium_total : info.premium_unlocked}/{info.premium_total})
                        </Button>
                      </Link>
                    ) : (
                      <Link to="/pricing" className="mb-3">
                        <Button data-testid={`dash-unlock-${cat}`} className="w-full rounded-none bg-[#002FA7] hover:bg-[#001d6b] label-mono py-5">
                          <Lock className="w-4 h-4 mr-2" />Sblocca {info.premium_total} domande
                        </Button>
                      </Link>
                    )}
                    <Link to={`/free/${cat}`}>
                      <Button data-testid={`dash-free-${cat}`} variant="outline" className="w-full rounded-none border-slate-900 label-mono py-5">
                        12 domande gratuite
                      </Button>
                    </Link>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="history" className="mt-8">
            {attempts.length === 0 ? (
              <p className="text-slate-500">Nessun tentativo ancora. Inizia un test!</p>
            ) : (
              <div className="border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 label-mono text-slate-500">
                    <tr><th className="text-left p-4">Test</th><th className="text-left p-4">Categoria</th><th className="text-right p-4">Punteggio</th><th className="text-right p-4">Data</th></tr>
                  </thead>
                  <tbody>
                    {attempts.map(a => (
                      <tr key={a.id} className="border-t border-slate-200">
                        <td className="p-4 font-medium">{a.test_title}</td>
                        <td className="p-4 text-slate-600">{CAT_LABEL[a.category]}</td>
                        <td className="p-4 text-right font-mono-num font-bold text-[#002FA7]">{a.score_percent}%</td>
                        <td className="p-4 text-right text-slate-500 font-mono-num text-xs">{new Date(a.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="purchases" className="mt-8">
            {(user.purchases || []).length === 0 ? (
              <div>
                <p className="text-slate-500 mb-4">Nessun acquisto ancora.</p>
                <Link to="/pricing"><Button className="rounded-none bg-[#002FA7] hover:bg-[#001d6b] label-mono">Vai al listino</Button></Link>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {(user.purchases || []).map(p => (
                  <span key={p} className="border border-slate-200 bg-white px-3 py-2 label-mono text-xs">{p}</span>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="border border-slate-200 bg-white p-6 flex items-center justify-between">
      <div>
        <div className="label-mono text-slate-500 mb-2">{label}</div>
        <div className="font-display font-black text-4xl">{value}</div>
      </div>
      <Icon className="w-10 h-10 text-[#002FA7] opacity-30" strokeWidth={1.5} />
    </div>
  );
}
